import { useState } from 'react'

import {
  GetSupplementInsightsDocument,
  GetSupplementInsightsQuery,
} from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'

import { UPDATE_DATA } from 'modules/payroll_bundle/lib/constants'
import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'
import { useSupplementsInsights } from 'modules/payroll_bundle/pages/SupplementsPage/supplementsInsightsContext'
import {
  SupplementsByTaxonomy,
  TotalByCurrency,
} from 'modules/payroll_bundle/pages/SupplementsPage/supplementsInsightsContext'

export type SupplementInsights = NonNullable<
  GetSupplementInsightsQuery['payroll']['supplementInsightsConnection']
>['nodes'][number]

const useFetchSupplementInsights = ({ triggerFetch = false }: { triggerFetch?: boolean }) => {
  const { insightsDispatch } = useSupplementsInsights()
  const [isLoading, setIsLoading] = useState(true)

  const { currentPeriod, prevPeriod } = usePolicyPeriods()

  const { refetch: currentPeriodRefetch, loading: currentPeriodLoading } = useQuery(
    GetSupplementInsightsDocument,
    {
      variables: {
        policyPeriodId: currentPeriod?.id,
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-first',
    }
  )

  const { refetch: previousPeriodRefetch, loading: previousPeriodLoading } = useQuery(
    GetSupplementInsightsDocument,
    {
      variables: {
        policyPeriodId: prevPeriod?.id,
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-first',
      skip: !prevPeriod?.id,
    }
  )

  const loading = currentPeriodLoading || previousPeriodLoading

  /**
   * NOTE: The `map`s to change the type of currency from string to Currency won't be needed
   * if we can fix the currency type generated from the GraphQL
   */
  const fetchData = useUnsafeCallback(async () => {
    let newPreviousPeriodSupplementInsights
    const { data: newData } = await currentPeriodRefetch()
    if (prevPeriod?.id) {
      const newPreviousPeriodData = await previousPeriodRefetch()
      newPreviousPeriodSupplementInsights =
        newPreviousPeriodData?.data?.payroll?.supplementInsightsConnection.nodes[0]
    }

    const newCurrentPeriodSupplementInsights =
      newData?.payroll?.supplementInsightsConnection.nodes[0]

    const payload = {
      legalEntityCount: newCurrentPeriodSupplementInsights?.legalEntityCount,
      currentPeriodSupplementCount: newCurrentPeriodSupplementInsights?.supplementCount,
      currentPeriodDraftSupplementCount: newCurrentPeriodSupplementInsights?.draftSupplementCount,
      totalByCurrency: newCurrentPeriodSupplementInsights?.totalByCurrency.map(
        ({ currency, total }) => ({ currency, total })
      ),
      totalByTaxonomy: newCurrentPeriodSupplementInsights?.totalByTaxonomy.map((it) => ({
        ...it,
        legalEntityCurrency: it.legalEntityCurrency,
      })),
      totalByEmployee: newCurrentPeriodSupplementInsights?.totalByEmployee.reduce<
        Record<string, number>
      >((acc, { employeeId, total }) => {
        acc[employeeId] = total
        return acc
      }, {}),
    }

    insightsDispatch({
      type: UPDATE_DATA,
      payload,
    })

    if (prevPeriod?.id) {
      const prevPeriodPayload = {
        previousPeriodSupplementCount: newPreviousPeriodSupplementInsights?.supplementCount || 0,
        previousPeriodTotalByCurrency: newPreviousPeriodSupplementInsights?.totalByCurrency.map(
          ({ currency, total }) => ({ currency, total })
        ),
        previousPeriodTotalByTaxonomy: newPreviousPeriodSupplementInsights?.totalByTaxonomy.map(
          (it) => ({
            ...it,
            legalEntityCurrency: it.legalEntityCurrency,
          })
        ),
        previousPeriodTotalByEmployee: newPreviousPeriodSupplementInsights?.totalByEmployee.reduce<
          Record<string, number>
        >((acc, { employeeId, total }) => {
          acc[employeeId] = total
          return acc
        }, {}),
      }
      insightsDispatch({
        type: UPDATE_DATA,
        payload: prevPeriodPayload,
      })
    }
    setIsLoading(false)
  }, [currentPeriodRefetch, prevPeriod?.id, insightsDispatch, previousPeriodRefetch])

  useEffect(() => {
    triggerFetch && !loading ? fetchData() : setIsLoading(loading)
  }, [currentPeriod?.id, triggerFetch, fetchData, loading])

  return {
    loading: isLoading,
    fetchInsightsData: fetchData,
  }
}
export default useFetchSupplementInsights

export const totalByTaxonomyForPreviousPeriod = (
  previousTotalByTaxonomy: SupplementsByTaxonomy[],
  taxonomy_id: string
) => {
  if (previousTotalByTaxonomy.length > 0) {
    return previousTotalByTaxonomy.find((item) => item.taxonomyId === taxonomy_id)?.total
  }
  return 0
}

export const totalByCurrencyForPreviousPeriod = (
  previousTotalByCurrency: TotalByCurrency[],
  currency: string
) => {
  return previousTotalByCurrency.reduce((acc: number, totalByCurrency) => {
    if (totalByCurrency.currency === currency) {
      return acc + totalByCurrency.total
    }

    return acc
  }, 0)
}
